import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import authService from '../../services/authService';
import '../admin/Admin.css';
import '../auth/Auth.css';

const packages = [
  { id: 'p5', amountCents: 500, creditCents: 500, label: '$5 → $5 in tokens' },
  { id: 'p25', amountCents: 2500, creditCents: 3000, label: '$25 → $30 in tokens (20% bonus!)' },
  { id: 'p50', amountCents: 5000, creditCents: 6500, label: '$50 → $65 in tokens (30% bonus!)' }
];

const TopUpInner = ({ publishableKey }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState('p25');
  const [status, setStatus] = useState('');
  const [pending, setPending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const selected = useMemo(() => packages.find(p => p.id === selectedId) || packages[1], [selectedId]);

  useEffect(() => {
    (async () => {
      try {
        const u = await authService.getUserInfo();
        const email = (u && (u.Email || u.email || '')) || '';
        setUserEmail(email);
        if (email && email.toLowerCase().startsWith('testdrive')) {
          setBlocked(true);
          setStatus('Top-ups are disabled for TestDrive accounts. Use Admin → Billing → Apply Adjust.');
        }
      } catch {}
    })();
  }, []);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!stripe || !elements) return;
    // Hide confirmation modal once user confirms
    try { setShowConfirm(false); } catch {}
    setStatus('');
    setPending(true);
    try {
      const t = authService.getToken && authService.getToken();
      const res = await fetch('/api/payments/one-time-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ AmountCents: selected.amountCents, PackageId: selected.id })
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Failed to create payment');
      }
      const { clientSecret } = await res.json();
      const card = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(clientSecret, { payment_method: { card }, return_url: window.location.origin + '/top-up' });
      if (result.error) {
        setStatus(result.error.message || 'Payment failed');
      } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        setStatus('Success! Applying credits…');
        try {
          const piId = result.paymentIntent.id;
          const t2 = authService.getToken && authService.getToken();
          const rVerify = await fetch(`/api/payments/verify?paymentIntentId=${encodeURIComponent(piId)}`,
            { credentials:'include', headers: t2 ? { Authorization: `Bearer ${t2}` } : {} });
          if (rVerify.ok) {
            setStatus('Success! Credits applied.');
            try { localStorage.setItem('justUpgradedPro', '1'); } catch {}
          } else {
            // Fall back to webhook timeline if verify is blocked
            const body = await rVerify.text();
            const msg = (body && body.length < 120 ? body : null) || 'Payment succeeded. Credits will be added shortly.';
            setStatus(msg);
          }
        } catch {
          // Ignore and rely on webhook
        }
        // Signal other tabs/pages (dashboard) to refresh balance immediately
        try {
          localStorage.setItem('balanceRefreshTick', String(Date.now()));
          // Fire a second signal shortly after to catch webhook-applied credits
          setTimeout(() => {
            try { localStorage.setItem('balanceRefreshTick', String(Date.now())); } catch {}
          }, 1500);
        } catch {}
      } else {
        setStatus('Payment processing.');
      }
    } catch (e) {
      setStatus(typeof e === 'string' ? e : (e.message || 'Payment failed'));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-section">
        <h2 className="admin-header">Top Up Tokens</h2>
        <div className="dashboard-card" style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{ marginBottom: 12 }}>
            {packages.map(p => (
              <label key={p.id} style={{ display:'block', marginBottom: 6 }}>
                <input type="radio" name="pkg" value={p.id} checked={selectedId===p.id} onChange={()=>setSelectedId(p.id)} />
                <span style={{ marginLeft: 8 }}>{p.label}</span>
              </label>
            ))}
          </div>
          <div style={{ marginBottom: 8, color:'#64748b' }}>Amount charged today: ${(selected.amountCents/100).toFixed(2)} • Credit added: ${(selected.creditCents/100).toFixed(2)}</div>
          <form onSubmit={handleSubmit}>
            <div style={{ border:'1px solid #e5e7eb', padding: 12, borderRadius: 8, background:'#fff' }}>
              <CardElement options={{ style: { base: { fontSize: '16px' } } }} />
            </div>
            <div style={{ display:'flex', gap:8, marginTop: 12, alignItems:'stretch' }}>
              <div style={{ flex: 1 }}>
                <button type="button" className="action-button secondary" style={{ width:'100%' }} onClick={()=>navigate('/dashboard')} disabled={pending}>Back to Dashboard</button>
              </div>
              <div style={{ flex: 3 }}>
                <button type="button" className="action-button primary" style={{ width:'100%' }} disabled={blocked || !stripe || pending} onClick={()=>setShowConfirm(true)}>{blocked ? 'Top Up Disabled' : (pending ? 'Processing…' : 'Pay & Top Up')}</button>
              </div>
            </div>
          </form>
          {status && (<div style={{ marginTop: 10, color: status.startsWith('Success') ? '#065f46' : (blocked ? '#92400e' : '#b91c1c') }}>{blocked ? 'Top-ups are disabled for TestDrive accounts. Contact Admin for additional test credits.' : status}</div>)}
        </div>
      </div>
      {showConfirm && (
        <div className="modal-backdrop" onClick={()=>!pending && setShowConfirm(false)}>
          <div className="app-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="app-modal-header">
              <strong>Confirm Pay & Top Up</strong>
              <button className="close" onClick={()=>!pending && setShowConfirm(false)} disabled={pending}>×</button>
            </div>
            <div className="app-modal-body">
              <div style={{ lineHeight:1.6 }}>
                <div>You are about to purchase <strong>${(selected.amountCents/100).toFixed(2)}</strong> in credit.</div>
                <div>Credit to be added: <strong>${(selected.creditCents/100).toFixed(2)}</strong>{selected.creditCents>selected.amountCents ? ` (includes $${((selected.creditCents-selected.amountCents)/100).toFixed(2)} bonus)` : ''}.</div>
                <div style={{ marginTop:8, color:'#64748b', fontSize:13 }}>Your card will be charged ${(selected.amountCents/100).toFixed(2)}. By continuing, you agree to our billing terms.</div>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:16, alignItems:'stretch' }}>
                <div style={{ flex:1 }}>
                  <button className="action-button secondary" style={{ width:'100%' }} onClick={()=>!pending && setShowConfirm(false)} disabled={pending}>Cancel</button>
                </div>
                <div style={{ flex:3 }}>
                  <button className="action-button primary" style={{ width:'100%' }} onClick={()=>{ if (blocked || pending || !stripe || !elements) return; try { setShowConfirm(false); } catch {}; handleSubmit(); }} disabled={blocked || pending || !stripe || !elements}>{blocked ? 'Top Up Disabled' : (pending ? 'Processing…' : 'Confirm Pay & Top Up')}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TopUp = () => {
  const [pubKey, setPubKey] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/payments/config');
        const j = await res.json();
        setPubKey(j?.publishableKey || '');
      } catch {}
    })();
  }, []);
  const stripePromise = useMemo(() => pubKey ? loadStripe(pubKey) : null, [pubKey]);
  if (!pubKey) return (<div className="admin-container"><div className="admin-section"><div className="dashboard-card">Loading…</div></div></div>);
  return (
    <Elements stripe={stripePromise}>
      <TopUpInner publishableKey={pubKey} />
    </Elements>
  );
};

export default TopUp;


